import { useState, useCallback, useEffect } from 'react'
import { IOS_APP_STORE_LINK, ANDROID_PLAY_STORE_LINK } from 'constants/appStores'
import { FF_APP_VERSION, appVersions } from 'constants/featureFlags'
import useFeatureFlag from 'hooks/useFeatureFlag'
import useFeatureFlagContext from 'hooks/useFeatureFlagContext'
import { useJamesApolloQuery } from 'hooks/useJamesApolloQuery'
import info from 'info.json'
import { isNativeNoOverride, isIphoneNative, isAndroidNative } from 'utils/platform'
import { lastRecommendedVersionLocalStorageKey, versionRequirementQuery } from './constants'
import type { VersionNumberObjectT } from './types'
import { getMostRecentVersion, saveToLocalStorage } from './utils'

const useUpdateApp = () => {
  const FFVersion = useFeatureFlag(FF_APP_VERSION)
  const { settings: setFeatureFlag } = useFeatureFlagContext()
  const currentVersion = info.version
  const { data, loading } = useJamesApolloQuery<{ versionRequirement: VersionNumberObjectT }>(versionRequirementQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { versionNumber: currentVersion },
  })
  const [dialogSeen, setDialogSeen] = useState(false)
  const [recommendedVersion, setRecommendedVersion] = useState<string>(currentVersion)
  const [requiredVersion, setRequiredVersion] = useState<string>(currentVersion)

  const redirect = useCallback(() => {
    if (isNativeNoOverride()) {
      if (isIphoneNative()) {
        window.location.href = IOS_APP_STORE_LINK
      }
      if (isAndroidNative()) {
        window.location.href = ANDROID_PLAY_STORE_LINK
      }
    } else {
      window.location.reload()
    }
  }, [])

  const dismissDialog = useCallback(() => {
    FFVersion !== appVersions.current && setFeatureFlag(FF_APP_VERSION, appVersions.current)
    setDialogSeen(true)
    saveToLocalStorage(recommendedVersion)
  }, [FFVersion, recommendedVersion, setFeatureFlag])

  useEffect(() => {
    setFeatureFlag(FF_APP_VERSION, appVersions.current)
  }, [redirect, setFeatureFlag])

  useEffect(() => {
    if (loading || !data) return

    const {
      versionRequirement: { recommendedVersion: recommendedVersionData, requiredVersion: requiredVersionData },
    } = data

    setRecommendedVersion(recommendedVersionData)
    setRequiredVersion(requiredVersionData)
    setDialogSeen(localStorage.getItem(lastRecommendedVersionLocalStorageKey) === recommendedVersionData)
  }, [loading, data])

  const meetsRequired = getMostRecentVersion(currentVersion, requiredVersion) === currentVersion
  const meetsRecommended = getMostRecentVersion(currentVersion, recommendedVersion) === currentVersion
  const showDialog = (!meetsRecommended && !dialogSeen) || FFVersion === appVersions.beforeRecommended

  return {
    loading,
    data,
    redirect,
    dismissDialog,
    meetsRequired,
    showDialog,
    meetsRecommended,
    FFVersion,
  }
}

export default useUpdateApp
